import { flagColorHex } from "@/lib/container-flags";
import type { LabelFieldKey, LabelSizeDefinition, LabelSizeKey } from "@/types/label-template";
import { labelFieldValue, NO_VALUE, type LabelFieldValues } from "./label-fields";

/**
 * The sticker as a printable document.
 *
 * "Print label doesn't do anything — I need to finish that" (T-1003). It does
 * now, and it does it by building a whole HTML document rather than by printing
 * the page: a label is a fixed piece of stock, and the browser needs an `@page`
 * of exactly that size or it lays the sticker out on Letter and clips it.
 *
 * Kept pure — a string in, a string out — so what comes out of the printer can
 * be asserted on without a printer.
 */

/** Fallback if a client somehow prints before the catalog arrives. */
const FALLBACK_SIZES: readonly LabelSizeDefinition[] = [
  { key: "1x3", label: '1" x 3" prep label', widthIn: 3, heightIn: 1 },
  { key: "4x6", label: '4" x 6" case label', widthIn: 4, heightIn: 6 },
];

export function labelSizeInches(
  size: LabelSizeKey,
  catalog: readonly LabelSizeDefinition[] = FALLBACK_SIZES,
): { widthIn: number; heightIn: number } {
  const found = (catalog.length > 0 ? catalog : FALLBACK_SIZES).find((s) => s.key === size);
  return found ?? FALLBACK_SIZES[0]!;
}

/**
 * What the allergen line says when the recipe lists none.
 *
 * Not an em dash. On a food label a dash reads as "we didn't fill this in",
 * which is the one thing an allergen line must never be ambiguous about — so it
 * states what is true: this recipe lists none. It does not claim allergen-free.
 */
export function formatAllergens(names: readonly string[] | undefined, noneListed: string): string {
  const listed = (names ?? []).map((n) => n.trim()).filter((n) => n !== "");
  return listed.length > 0 ? listed.join(", ") : noneListed;
}

const ESCAPES: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#39;",
};

/** A recipe named `Ben & Jerry's <house>` must print, not break the document. */
function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (c) => ESCAPES[c]!);
}

export interface LabelDocumentOptions {
  fields: LabelFieldKey[];
  values: LabelFieldValues;
  /** Translated field names, keyed the same way the template is. */
  fieldNames: Record<LabelFieldKey, string>;
  title: string;
  size: LabelSizeKey;
  /** The catalog from the API, so the inches are never hard-coded here. */
  sizes?: readonly LabelSizeDefinition[];
  /**
   * The colours flagged on this recipe's containers, printed as ink beside the
   * words (T-1005). A cook holding the sticker against a lid matches the colour
   * before they read anything, so the dot has to survive the printer.
   */
  flagColors?: readonly string[];
  /** Translated, because the printed sticker is read by the same people. */
  copy: { documentTitle: string; allergensPrefix: string };
  /**
   * Organization branding (T-1301): the logo prints beside the name and the
   * primary colour inks the name. Unset prints the label exactly as before —
   * black on white. Kept out of the allergen box on purpose: that stays black
   * ink in a black border whatever the brand looks like.
   */
  branding?: { logoUrl?: string | null; primaryColor?: string | null };
}

/**
 * Build the document one label prints as.
 *
 * The two stocks are not the same label at two scales. A 1x3 has room for a name
 * and a few short lines, so it is set tight and small; a 4x6 has room to be read
 * across a walk-in, so it is set large. Both put allergens last and boxed —
 * "allergens must be on the label" (transcript 51:00), and last is where a hand
 * covering the sticker is least likely to hide them.
 */
export function buildLabelDocument({
  fields,
  values,
  fieldNames,
  title,
  size,
  sizes,
  flagColors,
  copy,
  branding,
}: LabelDocumentOptions): string {
  const { widthIn, heightIn } = labelSizeInches(size, sizes);
  const small = size === "1x3";

  const nameColor = branding?.primaryColor ?? "#000";
  const logo = branding?.logoUrl
    ? `<img class="logo" src="${escapeHtml(branding.logoUrl)}" alt="">`
    : "";

  // The name is the heading. Allergens are pulled out of the flow so they print
  // in their own box whatever order the template happens to list them in.
  const rows = fields.filter((f) => f !== "RECIPE_NAME" && f !== "ALLERGENS");
  const allergens = fields.includes("ALLERGENS")
    ? labelFieldValue("ALLERGENS", values)
    : null;

  const dots =
    (flagColors ?? [])
      .map((c) => `<span class="dot" style="background:${escapeHtml(flagColorHex(c))}"></span>`)
      .join("") || "";

  const line = (field: LabelFieldKey) =>
    `<div class="row"><span class="k">${escapeHtml(fieldNames[field])}</span>` +
    `<span class="v">${field === "CONTAINER_FLAG" ? dots : ""}` +
    `${escapeHtml(labelFieldValue(field, values))}</span></div>`;

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>${escapeHtml(copy.documentTitle)}</title>
<style>
  @page { size: ${widthIn}in ${heightIn}in; margin: 0; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body {
    width: ${widthIn}in;
    height: ${heightIn}in;
    padding: ${small ? "0.06in" : "0.2in"};
    font-family: Helvetica, Arial, sans-serif;
    font-size: ${small ? "7pt" : "12pt"};
    line-height: 1.25;
    color: #000;
    display: flex;
    flex-direction: column;
    gap: ${small ? "0.02in" : "0.1in"};
  }
  .name {
    font-size: ${small ? "10pt" : "22pt"};
    font-weight: 700;
    line-height: 1.1;
    color: ${nameColor};
    /* Two lines, then cut. A name that pushes the use-by off the sticker is
       worse than a name that is abbreviated. */
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
  .head { display: flex; justify-content: space-between; align-items: flex-start; gap: 0.06in; }
  .head .name { flex: 1; min-width: 0; }
  .logo {
    /* Small enough that a logo never crowds a use-by date off the stock. */
    height: ${small ? "0.18in" : "0.45in"};
    max-width: 30%;
    object-fit: contain;
    flex-shrink: 0;
  }
  .rows { display: flex; flex-direction: column; gap: ${small ? "0.01in" : "0.06in"}; }
  .row { display: flex; justify-content: space-between; gap: 0.08in; }
  .k { color: #000; opacity: 0.75; white-space: nowrap; }
  .v { font-weight: 600; text-align: right; overflow: hidden; }
  .dot {
    display: inline-block;
    width: ${small ? "6pt" : "11pt"};
    height: ${small ? "6pt" : "11pt"};
    border-radius: 50%;
    /* Outlined: a yellow dot on white stock is otherwise not there at all. */
    border: 0.5pt solid #000;
    margin-right: 0.03in;
    vertical-align: middle;
  }
  .allergens {
    margin-top: auto;
    border: ${small ? "0.5pt" : "1.5pt"} solid #000;
    border-radius: ${small ? "2pt" : "5pt"};
    padding: ${small ? "0.02in 0.04in" : "0.06in 0.1in"};
    font-weight: 700;
    font-size: ${small ? "7pt" : "13pt"};
    text-transform: uppercase;
  }
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
</style>
</head>
<body>
  <div class="head"><div class="name">${escapeHtml(title || NO_VALUE)}</div>${logo}</div>
  <div class="rows">${rows.map(line).join("")}</div>
  ${
    allergens === null
      ? ""
      : `<div class="allergens">${escapeHtml(copy.allergensPrefix)} ${escapeHtml(allergens)}</div>`
  }
</body>
</html>`;
}
